import { useScanEditingContextSlice } from '@/Client/State';
import { LAYOUT2 } from '@/Client/Styles';
import { LABELED_INPUT_STEPPER_FLOOR, LABELED_SELECT_STYLES, LabeledInput, LabeledInputSize, LabeledInputStyle, LabeledSelect } from '@/Client/Widgets';
import { capitalize, dashCase } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { getScanValueInputString, getScanValueOverrideTooltip } from '@kaiizen/kai-presenters';
import {
  getEffectiveScanValue,
  getUpdatedScanValue,
  isExpressionValue,
  isNumberScanValue,
  ScanTemplateValue,
  ScanValue,
} from '@kaiizen/kai-scan-template-system';
import { Checkbox, FormControlLabel } from '@mui/material';
import { FocusedScanValueDefaultCaption, getScanValueOverridesRealDefault, SCAN_VALUE_OVERRIDE_COLOR } from './FocusedScanValueDefaultCaption';
import { FOCUSED_TASK_CARD_LABEL, FOCUSED_TASK_CARD_VALUE_FULL_WIDTH } from './focusedTaskCardStyles';

/** The longest a text value may be; templates set no length limit of their own. */
export const MAX_SCAN_TEXT_VALUE_LENGTH = 1000;

const LABELED_FIELD_STYLE = css`
  label: LABELED_FIELD_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacingHalf}px;
  min-width: 0;
`;

// Component to display a single value
export const FocusedScanValueView = ({
  valueKey,
  value,
  templateValue,
  updateValue,
  size = 'small',
  style = 'normal',
  backgroundColor,
  width,
  label,
  tabIndex,
  disabled = false,
  noLabel = false,
  step,
  tooltipTitle,
  presentsOverride = true,
}: {
  valueKey: string;
  value?: ScanValue;
  templateValue: ScanTemplateValue;
  updateValue: (key: string, newValue: ScanValue) => void;
  size?: LabeledInputSize;
  style?: LabeledInputStyle;
  width?: string;
  backgroundColor?: string;
  label?: string;
  tabIndex?: number;
  disabled?: boolean;
  /** Render only the field itself; the caller owns the label and cell chrome. */
  noLabel?: boolean;
  /** A count: the field gets a +/− stepper of this much, floors at zero, and shows no unit (its label carries the unit). */
  step?: number;
  /** Heads the tooltips about the value (its default caption's); defaults to the value's key, presented. */
  tooltipTitle?: string;
  /**
   * Whether a value differing from its default reads as an override (red frame, default caption).
   * Off for a value the user is simply meant to set, such as an unmeasured line's quantity.
   */
  presentsOverride?: boolean;
}) => {
  const { scanIsEditable } = useScanEditingContextSlice(['scanIsEditable']);
  if (!templateValue) return null;

  const effectiveValue = getEffectiveScanValue(value);
  const valueType = templateValue?.type || '';
  const isTextValueType = valueType === 'text';
  const isCheckboxValueType = valueType === 'checkbox';
  const isNumericValueType = isNumberScanValue(templateValue);

  if (!scanIsEditable) disabled = true;

  const overridesRealDefault = presentsOverride && getScanValueOverridesRealDefault(value);

  const valueKeyPresented = dashCase(valueKey, ' ');
  const inlineUsd = style === 'taskCardField' && templateValue.type === 'usd';

  let fieldContents: React.ReactNode | undefined;

  if (isCheckboxValueType) {
    const isChecked = effectiveValue != null ? Boolean(effectiveValue) : undefined;

    fieldContents = (
      <FormControlLabel
        control={
          <Checkbox
            checked={isChecked}
            onChange={e => {
              updateValue(valueKey, getUpdatedScanValue(templateValue, value, e.target.checked));
            }}
          />
        }
        label={label ?? valueKeyPresented}
        title={overridesRealDefault ? getScanValueOverrideTooltip(value, templateValue) : undefined}
        sx={{
          // Override MUI's default margin with important
          margin: '0 !important',
          marginBottom: '0 !important',
          // Checkboxes have fixed width for multi-column layout
          width: '180px',
          minWidth: '150px',
          maxWidth: '200px',
          flexGrow: 0,
          flexShrink: 0,
          height: '44px', // Updated to iOS-friendly height
          alignItems: 'center',
        }}
      />
    );
  } else if (templateValue.valueOptions) {
    fieldContents = (
      <LabeledSelect
        label={label ?? valueKeyPresented}
        title={templateValue?.description}
        value={effectiveValue != null ? String(effectiveValue) : ''}
        items={templateValue.valueOptions.map(option => ({ value: option.value?.toString() ?? '', label: option.label }))}
        onChange={val => updateValue(valueKey, getUpdatedScanValue(templateValue, value, val))}
        tabIndex={tabIndex}
        styles={LABELED_SELECT_STYLES}
      />
    );
  } else {
    const inputType = inlineUsd ? 'text' : isNumericValueType ? 'number' : 'text';
    const isStepper = step !== undefined;
    // In a line item row, numbers and amounts right-align so their digits line up down the
    // column with the read-only values (a stepper's +/− column keeps to the left edge); the row's
    // text fields stay left-aligned.
    const isRightAligned = style === 'taskCardField' && isNumericValueType;
    const templateDefault = isExpressionValue(templateValue) ? '' : templateValue.value ?? null;
    // a stepper floors at zero however the number arrives: typed, arrowed, or stepped
    const updateTemplate = isStepper ? { ...templateValue, minValue: templateValue.minValue ?? LABELED_INPUT_STEPPER_FLOOR } : templateValue;

    width ??= '100%';

    fieldContents = (
      <>
        <LabeledInput
          frameColor={overridesRealDefault ? SCAN_VALUE_OVERRIDE_COLOR : undefined}
          title={templateValue?.description}
          size={size}
          width={width}
          backgroundColor={backgroundColor}
          unit={inlineUsd || isStepper ? undefined : templateValue.type !== 'text' ? templateValue.type : undefined}
          value={getScanValueInputString(effectiveValue, templateValue, inputType)}
          style={style === 'calculated' ? overridesRealDefault ? 'attention' : 'calculated' : style}
          onUpdate={disabled ? undefined : (localValue: string) => updateValue(valueKey, getUpdatedScanValue(updateTemplate, value, localValue))}
          step={step}
          disabled={disabled}
          type={inputType}
          textAlign={isRightAligned ? 'right' : undefined}
          multiline={templateValue.type === 'text'}
          maxLength={isTextValueType ? MAX_SCAN_TEXT_VALUE_LENGTH : undefined}
          placeholder={getScanValueInputString(templateDefault, templateValue, inputType)}
          tabIndex={tabIndex}
        />
        {presentsOverride && (
          <FocusedScanValueDefaultCaption
            valueKey={valueKey}
            value={value}
            templateValue={templateValue}
            updateValue={updateValue}
            underTaskCardField={style === 'taskCardField'}
            tooltipTitle={tooltipTitle ?? capitalize(valueKeyPresented)}
            disabled={disabled}
          />
        )}
      </>
    );
  }

  if (noLabel) return <>{fieldContents}</>;

  // free text gets a full-width textarea; every other field takes half the row
  const isFreeText = isTextValueType && !templateValue.valueOptions;
  return (
    <div css={[LABELED_FIELD_STYLE, isFreeText && FOCUSED_TASK_CARD_VALUE_FULL_WIDTH]}>
      <div css={FOCUSED_TASK_CARD_LABEL}>{label ?? valueKeyPresented}</div>
      {fieldContents}
    </div>
  );
};
